import { Asset, type Application } from 'playcanvas';
import { ensurePlayableGlb, retireDecoded } from './glbMeshopt';
interface Entry { asset: Asset | null; ready: Promise<Asset>; refs: number; settled: boolean; owned: boolean }
const caches = new WeakMap<Application, Map<string, Entry>>();
/** A GLB container is shared; model entities and animation controllers are per player. */
export function acquireAvatar(app: Application, url: string) {
    let cache = caches.get(app);
    if (!cache) { cache = new Map(); caches.set(app, cache); }
    let entry = cache.get(url);
    const cleanup = (e: Entry) => {
        if (e.refs || !e.settled || cache!.get(url) !== e) return;
        cache!.delete(url);
        if (e.owned && e.asset) { e.asset.unload(); app.assets.remove(e.asset); }
    };
    if (!entry) {
        const e: Entry = { asset: null, refs: 0, settled: false, owned: false, ready: Promise.resolve(null as unknown as Asset) };
        e.ready = (async () => {
            // meshopt/量化压缩模型在此换成浏览器内解码后的 Blob URL——对
            // 调用方透明：网络仍只传输压缩字节，引擎拿到的已是标准 GLB。
            const finalUrl = await ensurePlayableGlb(url);
            const existing = app.assets.getByUrl(finalUrl);
            const asset = existing ?? new Asset(finalUrl, 'container', { url: finalUrl });
            e.asset = asset;
            e.owned = !existing;
            const loaded = await new Promise<Asset>((resolve, reject) => {
                if (asset.loaded) { resolve(asset); return; }
                const off = () => { asset.off('load', onLoaded); asset.off('error', failed); };
                const onLoaded = () => { off(); resolve(asset); };
                const failed = (error: unknown) => { off(); reject(new Error(String(error))); };
                asset.once('load', onLoaded); asset.once('error', failed);
                if (!existing) app.assets.add(asset);
                app.assets.load(asset);
            });
            // 引擎解析完成：meshopt 转码 Blob 的字节已无人需要，退役撤销
            // 释放内存（spot 级解码产物 53.8MB；重载走重新转码，HTTP 缓存
            // 保证零网络成本）。延迟撤销见 retireDecoded。
            if (finalUrl !== url) retireDecoded(url);
            return loaded;
        })().finally(() => { e.settled = true; queueMicrotask(() => cleanup(e)); });
        cache.set(url, e);
        entry = e;
    }
    entry.refs++;
    const e = entry; let released = false;
    return { ready: e.ready, release: () => { if (!released) { released = true; e.refs--; cleanup(e); } } };
}
